import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import Router from "next/router";
import { dashboardRouterEvents, normalizeDashboardPathname } from "@/hooks/useDashboardRouter";
import { postFrontendMetric, reportedRoute } from "@/lib/reportWebVital";
type InFlight = {
  startedAt: number;
  url: string;
  fromRoute: string | null;
  fromPathname: string | undefined;
};
type Held = {
  startedAt: number;
  completedAt: number;
  route: string;
  flushTimer?: number;
};

// Module state rather than refs: App mounts once, and the markers below are
// called from page components that have no handle on the hook instance.
let inFlight: InFlight | null = null;
let held: Held | null = null;
let renderErrored = false;
let settleDeferred = false;

// Redirect-only pages (`/[org]`, `/[org]/runs`, OrgRedirect) replace from
// their mount effect, well inside this window.
const REDIRECT_CHAIN_WINDOW_MS = 100;
function post(route: string, durationMs: number): void {
  postFrontendMetric({
    navigation_duration_ms: String(durationMs),
    navigation_route: route
  });
}

// Next's `routeChange` mark precedes the manifest and middleware awaits that
// delay routeChangeStart, and is cleared after each render. jsdom has no
// User Timing API.
function navigationStartTime(): number {
  if (typeof performance.getEntriesByName !== "function") {
    return performance.now();
  }
  const mark = performance.getEntriesByName("routeChange", "mark").at(-1);
  return mark?.startTime ?? performance.now();
}

// Router state still describes the origin here; `reportedRoute()` only moves
// to the destination once `set()` runs, before routeChangeComplete.
function handleStart(url: string): void {
  let startedAt = navigationStartTime();
  if (held && isRedirectHop(held)) {
    window.clearTimeout(held.flushTimer);
    startedAt = held.startedAt;
  } else if (inFlight?.url === url) {
    startedAt = inFlight.startedAt;
  }
  held = null;
  renderErrored = false;
  settleDeferred = false;
  inFlight = {
    startedAt,
    url,
    fromRoute: reportedRoute(),
    fromPathname: Router.router?.pathname ?? normalizeDashboardPathname(location.pathname)
  };
}

// A deferred page that redirects from its mount effect holds no flush timer,
// so its hop is recognised by how recently it completed instead.
function isRedirectHop(held: Held): boolean {
  return held.flushTimer !== undefined || performance.now() - held.completedAt < REDIRECT_CHAIN_WINDOW_MS;
}

// The `shallow` flag is deliberately ignored: Next reports the caller's
// option, not whether the page actually changed, and the dashboard passes
// shallow on cross-route redirects while its query-only updates are not
// shallow. Comparing route patterns gets both right.
function handleComplete(url: string): void {
  const navigation = inFlight;
  inFlight = null;
  const route = reportedRoute();
  if (!route || !navigation || navigation.url !== url || route === navigation.fromRoute || renderErrored) {
    return;
  }
  // Moving between labels of one page (`/[org]/sessions/new` and a thread)
  // keeps that page's content mounted, so nothing later signals settle.
  const currentPathname = Router.router?.pathname ?? normalizeDashboardPathname(location.pathname);
  const remounted = currentPathname !== navigation.fromPathname;
  const completedAt = performance.now();
  if (settleDeferred && remounted) {
    held = {
      startedAt: navigation.startedAt,
      completedAt,
      route
    };
    return;
  }
  const durationMs = completedAt - navigation.startedAt;
  held = {
    startedAt: navigation.startedAt,
    completedAt,
    route,
    flushTimer: window.setTimeout(() => {
      held = null;
      post(route, durationMs);
    }, REDIRECT_CHAIN_WINDOW_MS)
  };
}

/**
 * Call during render of a page whose first commit is a placeholder for a
 * `next/dynamic` chunk; the loaded content then calls useNavigationSettled.
 */
export function deferNavigationSettle(): void {
  settleDeferred = true;
}
export function useNavigationSettled() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
}

/** Wired to the app ErrorBoundary so an error-page commit is not timed as the destination. */
function _temp() {
  if (!held || held.flushTimer !== undefined || held.route !== reportedRoute()) {
    return;
  }
  post(held.route, performance.now() - held.startedAt);
  held = null;
}
export function markNavigationRenderError(): void {
  renderErrored = true;
}

/**
 * Times each client transition between two route patterns from Next's
 * routeChange mark to the destination's first commit (or, for pages that
 * defer, to their content mount). Transitions that cancel, error, or never
 * leave their route pattern are dropped; a redirect stub's hop is charged to
 * the page it lands on.
 */
export function useNavigationTimingReporting() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
}
function _temp2() {
  const events = Router.router ? Router.events : dashboardRouterEvents;
  events.on("routeChangeStart", handleStart);
  events.on("routeChangeComplete", handleComplete);
  return () => {
    events.off("routeChangeStart", handleStart);
    events.off("routeChangeComplete", handleComplete);
  };
}